// Column definitions for the Glide Data Grid views

import { formatEventDate } from '../utils/dates';

export interface GridColumnDef {
  id: string;
  title: string;
  width: number;
  field: string;
  cellKind: 'text' | 'boolean' | 'bubble';
  formatFn?: (val: any, row: any) => string;
}

// ── Formatting helpers ───────────────────────────────────────────────────────

function fmtDate(val: any): string {
  if (!val) return '';
  return formatEventDate(String(val)) ?? String(val);
}

function fmtCurrency(val: any): string {
  if (val == null) return '';
  const n = Number(val);
  if (isNaN(n)) return String(val);
  return `$${n.toLocaleString()}`;
}

// ── Column width helper ─────────────────────────────────────────────────────

const WIDTH_OVERRIDES: Record<string, number> = {
  contact_name: 160,
  contact_email: 200,
  account_name: 160,
  intel_notes: 200,
  goer_notes: 200,
  general_notes: 200,
  reasoning: 200,
  suppression_reasons: 180,
  account_suppression_reasons: 180,
};

function colWidth(id: string, title: string): number {
  return WIDTH_OVERRIDES[id] ?? Math.max(title.length * 9, 80);
}

// ── Helper to define a column ───────────────────────────────────────────────

function col(
  id: string,
  title: string,
  opts?: { cellKind?: 'text' | 'boolean' | 'bubble'; formatFn?: (val: any, row: any) => string; width?: number },
): GridColumnDef {
  return {
    id,
    title,
    field: id,
    width: opts?.width ?? colWidth(id, title),
    cellKind: opts?.cellKind ?? 'text',
    formatFn: opts?.formatFn,
  };
}

// ── ALL FIELDS — master registry of event-member columns ───────────────────

export const ALL_FIELDS_COLUMNS: GridColumnDef[] = [
  // Contact + Status (high-priority columns first)
  col('cm_status', 'CM Status'),
  col('contact_name', 'Name'),
  col('contact_title', 'Title'),
  col('account_name', 'Account'),
  col('sales_tier', 'Sales Tier'),
  col('account_type', 'Account Type', { cellKind: 'bubble' }),
  col('contact_email', 'Email'),
  col('contact_linkedin', 'LinkedIn'),
  // Account (continued)
  col('product_tier', 'Product Tier'),
  col('plan_interval', 'Plan'),
  col('arr_active_subscriptions', 'ARR Subscriptions', { formatFn: (v) => fmtCurrency(v) }),
  col('arr_opportunity', 'ARR Opportunity', { formatFn: (v) => fmtCurrency(v) }),
  col('upcoming_renewal_close_date', 'Upcoming Renewal', { formatFn: (v) => fmtDate(v) }),
  col('number_of_open_opps', 'Open Opps'),
  col('open_pipeline', 'Open Pipeline', { formatFn: (v) => fmtCurrency(v) }),
  col('partner_type', 'Partner Type'),
  // Owners
  col('gtme_owner_name', 'GTME Owner'),
  col('gs_owner_name', 'GS Owner'),
  col('claydr_name', 'ClayDR'),
  // Event
  col('role', 'Role'),
  col('attending_status', 'Attending'),
  col('has_responded', 'Has Responded', { cellKind: 'boolean' }),
  col('table_id', 'Table'),
  // Owner flags
  col('owner_exclusion', 'Owner Exclusion', { cellKind: 'boolean' }),
  col('owner_priority', 'Owner Priority', { cellKind: 'boolean' }),
  // Logistics
  col('cal_invite_sent', 'Cal Sent', { cellKind: 'boolean' }),
  col('cal_invite_accepted', 'Cal Accepted', { cellKind: 'boolean' }),
  col('dietary_restrictions', 'Dietary'),
  // Meetings
  col('last_gtme_meeting_date', 'Last GTME Meeting', { formatFn: (v) => fmtDate(v) }),
  col('last_gs_meeting_date', 'Last GS Meeting', { formatFn: (v) => fmtDate(v) }),
  col('last_claydr_meeting_date', 'Last ClayDR Meeting', { formatFn: (v) => fmtDate(v) }),
  // Suppression
  col('suppressed', 'Contact Suppressed', { cellKind: 'boolean' }),
  col('suppression_reasons', 'Contact Suppression Reasons'),
  col('account_suppressed', 'Acct Suppressed', { cellKind: 'boolean' }),
  col('account_suppression_reasons', 'Acct Suppression Reasons'),
  // Notes
  col('intel_notes', 'Pre-Event Notes'),
  col('goer_notes', 'Met With Notes'),
  col('general_notes', 'General Notes'),
  // Post-event
  col('primary_met_with', 'Met w/ Claymate 1'),
  col('secondary_met_with', 'Met w/ Claymate 2'),
  col('tertiary_met_with', 'Met w/ Claymate 3'),
  col('follow_up_owner', 'Follow-up Owner'),
  col('last_follow_up_date', 'Last Follow Up', { formatFn: (v) => fmtDate(v) }),
  // AI
  col('score', 'Score'),
  col('recommendation', 'Recommendation'),
  col('reasoning', 'Reasoning'),
  col('approved', 'Approved', { cellKind: 'boolean' }),
  col('gtme_notes', 'GTME Notes'),
];

// ── FOLLOW-UP columns (dispatch-specific) ──────────────────────────────────

export const FOLLOW_UP_COLUMNS: GridColumnDef[] = [
  // SFDC / Contact data
  col('cm_status', 'CM Status'),
  col('contact_name', 'Name'),
  col('contact_title', 'Title'),
  col('account_name', 'Account'),
  col('sales_tier', 'Sales Tier'),
  col('account_type', 'Account Type', { cellKind: 'bubble' }),
  col('contact_email', 'Email'),
  col('contact_linkedin', 'LinkedIn'),
  col('product_tier', 'Product Tier'),
  col('number_of_open_opps', 'Open Opps'),
  col('gtme_owner_name', 'GTME Owner'),
  col('gs_owner_name', 'GS Owner'),
  col('claydr_name', 'ClayDR'),
  // Dispatch data
  col('bucket', 'Bucket'),
  col('channel', 'Channel'),
  col('sender_email', 'Sender', { width: 200 }),
  col('subject', 'Subject', { width: 200 }),
  col('body', 'Body', { width: 300 }),
  col('result', 'Dispatch Result'),
  col('result_status', 'Result Status'),
  col('dispatched_at', 'Dispatched At', { formatFn: (v) => fmtDate(v) }),
  col('result_at', 'Result At', { formatFn: (v) => fmtDate(v) }),
];

// ── Combined column registry (all possible columns across all views) ───────

const COLUMN_REGISTRY = new Map<string, GridColumnDef>();
for (const c of ALL_FIELDS_COLUMNS) COLUMN_REGISTRY.set(c.id, c);
for (const c of FOLLOW_UP_COLUMNS) {
  if (!COLUMN_REGISTRY.has(c.id)) COLUMN_REGISTRY.set(c.id, c);
}

export function resolveColumns(columnIds: string[]): GridColumnDef[] {
  return columnIds
    .map(id => COLUMN_REGISTRY.get(id))
    .filter((c): c is GridColumnDef => c != null);
}

export function getColumnRegistry(): Map<string, GridColumnDef> {
  return COLUMN_REGISTRY;
}

// ── Default view presets (used for DB seeding) ─────────────────────────────

const PRE_EVENT_IDS = [
  'cm_status', 'contact_name', 'contact_title', 'account_name',
  'sales_tier', 'account_type',
  'contact_email',
  'product_tier', 'arr_active_subscriptions', 'arr_opportunity', 'upcoming_renewal_close_date',
  'gtme_owner_name', 'gs_owner_name', 'claydr_name',
  'attending_status', 'role',
  'suppressed', 'account_suppressed',
  'last_gtme_meeting_date', 'last_gs_meeting_date', 'last_claydr_meeting_date',
  'owner_exclusion', 'owner_priority',
  'cal_invite_sent', 'cal_invite_accepted',
  'intel_notes',
];

const DINNER_IDS = [
  ...PRE_EVENT_IDS,
  'table_id',
  'primary_met_with', 'secondary_met_with', 'tertiary_met_with',
  'dietary_restrictions',
  'goer_notes',
];

export const DEFAULT_VIEW_PRESETS: { name: string; slug: string; columns: string[] }[] = [
  { name: 'Pre-Event', slug: 'pre-event', columns: PRE_EVENT_IDS },
  { name: 'Post Event', slug: 'dinner', columns: DINNER_IDS },
  { name: 'All Fields', slug: 'all', columns: ALL_FIELDS_COLUMNS.map(c => c.id) },
  { name: 'Dispatch Logs', slug: 'follow-up', columns: FOLLOW_UP_COLUMNS.map(c => c.id) },
];

// ── Sort types ──────────────────────────────────────────────────────────────

export type SortDir = 'asc' | 'desc' | null;
export interface SortState {
  colIndex: number;
  dir: SortDir;
}

// ── Filter types & operator helpers ─────────────────────────────────────────

export type FilterOperator =
  | 'equals' | 'not_equal' | 'contains' | 'does_not_contain'
  | 'is_empty' | 'is_not_empty'
  | 'greater_than' | 'less_than'
  | 'is_true' | 'is_false';

export interface FilterRule {
  id: string;
  field: string;
  operator: FilterOperator;
  value: string;
}

interface OperatorOption {
  value: FilterOperator;
  label: string;
}

const TEXT_OPERATORS: OperatorOption[] = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equal', label: 'not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'does_not_contain', label: 'does not contain' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const NUMBER_OPERATORS: OperatorOption[] = [
  { value: 'equals', label: 'equals' },
  { value: 'greater_than', label: 'greater than' },
  { value: 'less_than', label: 'less than' },
  { value: 'is_empty', label: 'is empty' },
];

const BOOLEAN_OPERATORS: OperatorOption[] = [
  { value: 'is_true', label: 'is true' },
  { value: 'is_false', label: 'is false' },
];

const BUBBLE_OPERATORS: OperatorOption[] = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equal', label: 'not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'does_not_contain', label: 'does not contain' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

// Number fields that should use numeric operators
const NUMBER_FIELDS = new Set(['arr_active_subscriptions', 'arr_opportunity', 'score', 'number_of_open_opps', 'open_pipeline']);

export function getOperatorsForColumn(columnDef: GridColumnDef): OperatorOption[] {
  if (columnDef.cellKind === 'boolean') return BOOLEAN_OPERATORS;
  if (columnDef.cellKind === 'bubble') return BUBBLE_OPERATORS;
  if (NUMBER_FIELDS.has(columnDef.field)) return NUMBER_OPERATORS;
  return TEXT_OPERATORS;
}

export function operatorNeedsValue(op: FilterOperator): boolean {
  return !['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(op);
}
